import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "../styles/Auth.css"; // 保持统一样式

// 创建上下文
const AlertContext = createContext();

// Provider：全局管理 alert
export const AlertProvider = ({ children }) => {
  // 试过很多次，不能用 const { t } = useTranslation(["actions", "common"]); // ✅ 多命名空间：actions + common
  // ✅ 单独取 actions / common
  const { t: tActions } = useTranslation("actions");
  const { t: tCommon } = useTranslation("common");
  const [alert, setAlert] = useState(null);
  const confirmBtnRef = useRef(null);
  const prevActiveRef = useRef(null);

  /**
   * ✅ 显示 Alert（兼容原来的 8 个参数；可选第 9 个参数为 options 对象）
   */
  const showAlert = (
    title,
    message,
    confirmText = tActions("ok") || "我知道了",  // ✅ 默认就是翻译后的「我知道了// 原来的confirmText = t("actions.ok"),试过很多次不能用
    type = "info",
    onConfirm = null,
    cancelText = null,
    onCancel = null,
    autoClose = null,
    options = {}
  ) => {
    if (typeof autoClose === "object" && options == null) {
      options = autoClose;
      autoClose = null;
    }

    const {
      dismissOnBackdrop = false,
      backdropAction = "close",
      autoConfirmOnClose = false,
    } = options || {};

    setAlert({
      title,
      message,
      confirmText,
      type,
      onConfirm,
      cancelText,
      onCancel,
      autoClose,
      dismissOnBackdrop,
      backdropAction,
      autoConfirmOnClose,
    });
  };

  // ✅ 关闭 Alert
  const closeAlert = () => {
    setAlert(null);
  };

  // ✅ 点击确认按钮
  const handleConfirm = () => {
    if (alert && typeof alert.onConfirm === "function") {
      try {
        alert.onConfirm();
      } catch (err) {
        console.error("CustomAlert onConfirm error:", err);
      }
    }
    closeAlert();
  };

  // ✅ 点击取消按钮
  const handleCancel = () => {
    if (alert && typeof alert.onCancel === "function") {
      try {
        alert.onCancel();
      } catch (err) {
        console.error("CustomAlert onCancel error:", err);
      }
    }
    closeAlert();
  };

  // ✅ 键盘事件（Enter 确认，Esc 取消）
  useEffect(() => {
    if (!alert) return;

    const handleKeyDown = (e) => {
      if (e.key === "Enter") handleConfirm();
      if (e.key === "Escape") handleCancel();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [alert]);

  // ✅ 自动关闭（可选择自动时是否调用 onConfirm）
  useEffect(() => {
    if (alert && alert.autoClose) {
      const timer = setTimeout(() => {
        if (alert.autoConfirmOnClose) {
          handleConfirm();
        } else {
          closeAlert();
        }
      }, alert.autoClose);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // ✅ 打开时禁用背景滚动，聚焦确认按钮；关闭时恢复
  useEffect(() => {
    if (alert) {
      prevActiveRef.current = document.activeElement;
      document.body.style.overflow = "hidden";
      setTimeout(() => {
        confirmBtnRef.current?.focus?.();
      }, 0);
    } else {
      document.body.style.overflow = "";
      prevActiveRef.current?.focus?.();
    }
  }, [alert]);

  // ✅ 遮罩点击处理（可选）
  const onBackdropClick = () => {
    if (!alert?.dismissOnBackdrop) return;
    switch (alert.backdropAction) {
      case "confirm":
        handleConfirm();
        break;
      case "cancel":
        handleCancel();
        break;
      default:
        closeAlert();
    }
  };
  // ✅ 确认 i18n 已初始化（防止 actions.json 报错）
  if (!tActions || !tCommon) console.warn("i18n not initialized in CustomAlert");

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {alert && (
        <div
          className="custom-alert-backdrop"
          onClick={onBackdropClick}
          aria-hidden="true"
        >
          <div
            className={`custom-alert ${alert.type}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="custom-alert-title"
            aria-describedby="custom-alert-message"
            onClick={(e) => e.stopPropagation()}
          >
            {alert.title && (
              <h3 id="custom-alert-title" className="custom-alert-title">
                {alert.title}
              </h3>
            )}
            <p id="custom-alert-message" className="custom-alert-message">
              {alert.message}
            </p>
            <div className="custom-alert-actions">
              <button
                ref={confirmBtnRef}
                className={`button ${alert.type === "error" ? "error-btn" : ""}`}
                onClick={handleConfirm}
              >
                {alert.confirmText || tActions("ok") || "我知道了"}
              </button>
              {alert.cancelText && (
                <button className="button cancel-btn" onClick={handleCancel}>
                  {alert.cancelText}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AlertContext.Provider>
  );
};

// 自定义 hook
export const useAlert = () => useContext(AlertContext);
